import React, { createContext, useCallback, useContext, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { config } from "@src/constants"
import { getPartnerUuidFleets } from "@src/api"
import { usePartner } from "@src/partner"
import { usePersistedState } from "@src/utils"
import { FLEETS } from "./types"

type FLEET_CONTEXT = {
  loading: boolean
  fleet: FLEETS
  fleets: FLEETS[]
  updateFleets: () => Promise<FLEETS[]>
  updateFleetUuid: (id: string) => void
}

const DEFAULT_FLEET_CONTEXT = {
  loading: false,
  fleet: null,
  fleets: [],
  updateFleets: () => Promise.resolve([]),
  updateFleetUuid: () => {},
}

export const FleetContext = createContext<FLEET_CONTEXT>(DEFAULT_FLEET_CONTEXT)

export const FleetProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [fleets, setFleets] = usePersistedState<FLEETS[]>("@fleetsList", [])
  const [loading, setLoading] = useState<boolean>(false)
  const { partner } = usePartner()
  const navigate = useNavigate()
  const [fleetUuid, setFleetUuid] = usePersistedState<string>("@fleetUuid", "")
  const findFleet = (uuid: string, fleetsList = fleets) =>
    fleetsList.find((fleet) => fleet.uuid === uuid)

  let fleet: FLEETS = null
  try {
    fleet = findFleet(fleetUuid)
  } catch (error) {
    // throw error
  }

  const updateFleets = useCallback((): Promise<FLEETS[]> => {
    if (!partner?.uuid) return Promise.resolve([])
    setLoading(true)
    return getPartnerUuidFleets(partner.uuid, {
      page: 0,
      per_page: config.partnerFleetsItemsPerPage,
    })
      .then(({ response, data }) => {
        if (response.ok) {
          setFleets(data.fleets)
          return data.fleets
        } else if (response.status == 401) {
          console.log("error", response)
        } else {
          navigate(`/${partner.uuid}`)
        }
      })
      .finally(() => {
        setLoading(false)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partner?.uuid, setFleets]) // navigate causes this to re-render on every route change

  useEffect(() => {
    if (partner) updateFleets()
    else setFleets([])
  }, [partner, setFleets, updateFleets])

  const updateFleetUuid = (uuid?: string) => {
    if (!uuid) setFleetUuid("")
    else if (uuid !== fleetUuid && partner) {
      fleet = findFleet(fleetUuid)
      setFleetUuid(uuid)
    }
  }

  const context = {
    loading,
    fleet,
    fleets,
    updateFleets,
    updateFleetUuid,
  }

  return <FleetContext.Provider value={context}>{children}</FleetContext.Provider>
}

export const useFleets = () => useContext(FleetContext)
